<template>
  <div v-if="enabled && showError">
    <ErrorState
      :title="t('error.somethingWentWrong')"
      :error-message="error?.message"
      :description="t('error.description')"
      :actions="actions"
    />
  </div>
  <div v-else>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onErrorCaptured, ref, computed } from 'vue';
import ErrorState, { type ErrorStateAction } from '@/components/common/ErrorState.vue';
import { useI18n } from 'vue-i18n';
import { datadogLogs } from '@datadog/browser-logs';
import { isAxiosError } from 'axios';
import { handleError } from '@/utils/errorHandling';
const { t } = useI18n();

const copiedError = ref(false);
const error = ref<Error | null>(null);
const isDev = import.meta.env.DEV;
const enabled = !import.meta.env.VITE_DISABLE_ERROR_BOUNDARY;
const showError = computed<boolean>(() => error.value !== null);

const actions = computed<ErrorStateAction[]>(() => {
  if (error.value?.message) {
    return [{
      label: copiedError.value ? t('copied') : t('error.copyError'),
      onClick: copyError,
      options: {
        type: 'secondary',
        icon: copiedError.value ? 'check' : 'copy',
      },
    }, {
      label: t('error.refreshPage'),
      onClick: reload,
    }];
  }
  return [{
    label: t('error.refreshPage'),
    onClick: reload,
  }];
});

/**
 * Check if an error is critical enough to show the error boundary
 * Currently only shows for auth errors (401) that would prevent the app from functioning
 */
function isCriticalError(err: Error): boolean {
  // Only show error boundary for 401 (Unauthorized) errors
  // These typically mean the user's session is invalid and the app cannot function
  if (isAxiosError(err)) {
    return err.response?.status === 401;
  }
  return false;
}

onErrorCaptured((err) => {
  // Only show error boundary for critical errors (auth errors)
  if (isCriticalError(err)) {
    error.value = err;

    // Always log critical errors to Datadog
    if (isDev) {
      console.error('[ErrorBoundary] Critical error caught:', err);
    } else {
      datadogLogs.logger.error('[ErrorBoundary] Critical error', {
        error: {
          stack: err.stack,
          message: err.message,
        },
      });
    }

    // Stop propagation - this error is critical, show the boundary
    return false;
  }

  // For non-critical errors, handle them directly
  // These are likely JavaScript errors that won't reach TanStack Query
  // Show toast and log to Datadog, but don't show the error boundary
  if (isDev) {
    console.warn('[ErrorBoundary] Non-critical error caught:', err);
  }

  handleError(err);
  return false;
});

const reload = () => window.location.reload();

const copyError = () => {
  navigator.clipboard.writeText(error?.value?.message ?? '');
  copiedError.value = true;
};
</script>
